import { __awaiter } from '../../../../../_virtual/_tslib.js';
import { useState, useEffect, useCallback } from 'react';
import { isSameAddress } from '@dynamic-labs/wallet-connector-core';
import '@dynamic-labs/sdk-api';
import { logger } from '../../../shared/logger.js';
import '../../../config/LSSuffix.js';
import '@dynamic-labs/iconic';
import 'react/jsx-runtime';
import '../../../context/ViewContext/ViewContext.js';
import '@dynamic-labs/utils';
import '../../constants/colors.js';
import 'ethers';
import '../../../shared/consts/index.js';

const orderedSecondaryWalletAddresses = (wallets) => wallets.map((wallet) => wallet.address).sort();
const useWalletEventListeners = ({ computeConnectedStateForWallets, handleLogOut, multiWallet, multiWalletWidgetState, primaryWallet, secondaryWallets, selectedWalletConnector, selectedWalletWithAction, setSelectedWalletConnector, setSelectedWalletWithAction, setMultiWalletWidgetState, user, setPrimaryWalletId, connectWalletByAddressAndChain, }) => {
    const [loadingNetwork, setLoadingNetwork] = useState(true);
    const [network, setNetwork] = useState(undefined);
    useEffect(() => {
        const fetchNetwork = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                if (!primaryWallet)
                    return;
                const network = yield primaryWallet.connector.getNetwork();
                setNetwork(network);
            }
            catch (error) {
                /* istanbul ignore next */
                logger.debug(error);
            }
            finally {
                setLoadingNetwork(false);
            }
        });
        fetchNetwork();
    }, [primaryWallet === null || primaryWallet === void 0 ? void 0 : primaryWallet.id]);
    useEffect(() => {
        if (!primaryWallet) {
            return;
        }
        const seen = new Set();
        addPrimaryWalletEventListeners(seen);
        addSecondaryWalletEventListeners(seen);
        return () => {
            primaryWallet.connector.teardownEventListeners();
            for (const wallet of secondaryWallets) {
                wallet.connector.teardownEventListeners();
            }
        };
        // justification: we don't want to setup event listeners more than needed
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        user,
        primaryWallet === null || primaryWallet === void 0 ? void 0 : primaryWallet.address,
        primaryWallet === null || primaryWallet === void 0 ? void 0 : primaryWallet.id,
        multiWalletWidgetState,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        orderedSecondaryWalletAddresses(secondaryWallets).join(),
        selectedWalletConnector === null || selectedWalletConnector === void 0 ? void 0 : selectedWalletConnector.name,
        primaryWallet === null || primaryWallet === void 0 ? void 0 : primaryWallet.connector.isInitialized,
    ]);
    const handleAccountChangeWhenAwaitingAccountSwitch = useCallback((isPrimaryWallet, newAddress) => {
        const linkedSecondaryWallet = secondaryWallets.find((wallet) => isSameAddress(wallet.address, newAddress, wallet.chain));
        // this means the user is linking a new account, so we need proof of ownership...
        if (!linkedSecondaryWallet) {
            setMultiWalletWidgetState('awaiting_signature', undefined, 'linking_new_wallet');
            if (isPrimaryWallet && primaryWallet) {
                setSelectedWalletConnector(primaryWallet.connector);
            }
            return;
        }
        // ...otherwise, the user is switching primary accounts betweeen the same wallet
        setSelectedWalletWithAction(null);
        setPrimaryWalletId(linkedSecondaryWallet.id);
        setMultiWalletWidgetState('idle');
    }, [
        setMultiWalletWidgetState,
        primaryWallet,
        secondaryWallets,
        setSelectedWalletConnector,
        setSelectedWalletWithAction,
        setPrimaryWalletId,
    ]);
    const addPrimaryWalletEventListeners = useCallback((seen) => {
        if (!primaryWallet) {
            return;
        }
        seen.add(primaryWallet.connector.name);
        primaryWallet.connector.setupEventListeners({
            onAccountChange: (accounts) => __awaiter(void 0, void 0, void 0, function* () {
                const [newAddress] = accounts;
                const newWallet = secondaryWallets.find((wallet) => isSameAddress(wallet.address, newAddress, wallet.chain));
                if (isSameAddress(newAddress, primaryWallet.address, primaryWallet.chain)) {
                    setMultiWalletWidgetState('idle');
                }
                else if (!multiWallet) {
                    yield handleLogOut();
                    return;
                }
                else if (multiWalletWidgetState === 'awaiting_account_switch') {
                    handleAccountChangeWhenAwaitingAccountSwitch(true, newAddress);
                }
                else if (multiWalletWidgetState === 'idle' && newWallet) {
                    setPrimaryWalletId(newWallet.id);
                }
                else if (multiWalletWidgetState === 'idle' && !newWallet) {
                    if (user) {
                        setMultiWalletWidgetState('detected_new_wallet');
                        setSelectedWalletConnector(primaryWallet.connector);
                        yield computeConnectedStateForWallets();
                        return;
                    }
                    connectWalletByAddressAndChain(primaryWallet.connector.name, newAddress, primaryWallet.chain);
                }
                else {
                    logger.error(`Unexpected multiWalletWidgetState (${multiWalletWidgetState}) and onAccountChange combination. Resetting state to idle.`);
                    setMultiWalletWidgetState('idle');
                }
                yield computeConnectedStateForWallets();
            }),
            onChainChange: (chain) => __awaiter(void 0, void 0, void 0, function* () {
                setNetwork(parseInt(chain));
            }),
            onDisconnect: handleLogOut,
        });
    }, [
        primaryWallet,
        setMultiWalletWidgetState,
        multiWallet,
        user,
        computeConnectedStateForWallets,
        handleLogOut,
        connectWalletByAddressAndChain,
        multiWalletWidgetState,
        secondaryWallets,
        setSelectedWalletConnector,
        handleAccountChangeWhenAwaitingAccountSwitch,
        setPrimaryWalletId,
    ]);
    const addSecondaryWalletEventListeners = useCallback((seen) => {
        const secondaryListeners = {
            onAccountChange: () => __awaiter(void 0, void 0, void 0, function* () {
                if (multiWalletWidgetState === 'awaiting_account_switch') {
                    handleAccountChangeWhenAwaitingAccountSwitch(false, (selectedWalletWithAction === null || selectedWalletWithAction === void 0 ? void 0 : selectedWalletWithAction.wallet.address) || '');
                }
                yield computeConnectedStateForWallets();
            }),
            onDisconnect: () => __awaiter(void 0, void 0, void 0, function* () {
                yield computeConnectedStateForWallets();
            }),
        };
        if (selectedWalletConnector && !seen.has(selectedWalletConnector.name)) {
            seen.add(selectedWalletConnector.name);
            selectedWalletConnector.setupEventListeners(secondaryListeners);
        }
        for (const wallet of secondaryWallets) {
            if (seen.has(wallet.connector.name)) {
                continue;
            }
            seen.add(wallet.connector.name);
            wallet.connector.setupEventListeners(secondaryListeners);
        }
    }, [
        computeConnectedStateForWallets,
        selectedWalletConnector,
        selectedWalletWithAction,
        handleAccountChangeWhenAwaitingAccountSwitch,
        multiWalletWidgetState,
        secondaryWallets,
    ]);
    return {
        loadingNetwork,
        network,
        setNetwork,
    };
};

export { useWalletEventListeners };
